import React from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { TrendingUp, Users, Building2, Briefcase, Globe, AlertCircle } from 'lucide-react';
import {
  MARKET_STATS, MARKET_GROWTH, MARKET_SEGMENTS,
  STATE_DISTRIBUTION, KEY_TRENDS,
} from './marketData';

const fmt = (n) => n >= 1000 ? `${(n / 1000).toFixed(0)}k` : n.toLocaleString();

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-slate-50`}>
        <Icon size={18} className={color} />
      </div>
    </div>
  </div>
);

const ImpactBadge = ({ level }) => {
  const colors = {
    HIGH:   'bg-red-100 text-red-700',
    MEDIUM: 'bg-amber-100 text-amber-700',
    LOW:    'bg-green-100 text-green-700',
  };
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${colors[level] || 'bg-slate-100 text-slate-600'}`}>
      {level}
    </span>
  );
};

const CustomGrowthTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-800 border border-white/10 rounded-xl p-3 shadow-xl text-xs">
      <p className="text-slate-300 font-semibold mb-1">{label}</p>
      <p className="text-indigo-400">Spend: <span className="font-bold">${payload[0]?.value}B</span></p>
      <p className="text-emerald-400">Participants: <span className="font-bold">{fmt(payload[1]?.value)}</span></p>
    </div>
  );
};

export default function MarketDashboard() {
  const totalProviders = STATE_DISTRIBUTION.reduce((s, d) => s + d.providers, 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Australian NDIS Market Intelligence</h1>
        <p className="text-sm text-slate-500 mt-1">
          2024–25 market data · {MARKET_STATS.providers.toLocaleString()} active providers · Last updated May 2026
        </p>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={TrendingUp} label="Total NDIS Spend" value={`$${MARKET_STATS.totalSpend}B`} sub="2024–25 FY" color="text-indigo-600" />
        <StatCard icon={Users} label="Participants" value={MARKET_STATS.participants.toLocaleString()} sub="Active NDIS plans" color="text-emerald-600" />
        <StatCard icon={Building2} label="Providers" value={MARKET_STATS.providers.toLocaleString()} sub="Registered AUS" color="text-amber-600" />
        <StatCard icon={Briefcase} label="Workers" value={`${(MARKET_STATS.workers / 1000).toFixed(0)}k`} sub="Disability workforce" color="text-violet-600" />
        <StatCard icon={TrendingUp} label="YoY Growth" value={`+${MARKET_STATS.growth2024}%`} sub="2023 → 2024" color="text-rose-600" />
        <StatCard icon={Globe} label="Projected 2030" value={`$${MARKET_STATS.projectedSpend2030}B`} sub="+47% from today" color="text-cyan-600" />
      </div>

      {/* Growth chart + Segments */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Growth trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-slate-700 mb-1">NDIS Market Growth 2019–2026</h2>
          <p className="text-xs text-slate-400 mb-4">* 2025–2026 are NDIA projections</p>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={MARKET_GROWTH} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="partGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis yAxisId="left"  tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `$${v}B`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip content={<CustomGrowthTooltip />} />
              <Area yAxisId="left"  type="monotone" dataKey="spend"        name="Spend ($B)"    stroke="#6366f1" fill="url(#spendGrad)" strokeWidth={2} />
              <Area yAxisId="right" type="monotone" dataKey="participants" name="Participants"   stroke="#10b981" fill="url(#partGrad)"  strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Segments pie */}
        <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm flex flex-col">
          <h2 className="text-sm font-semibold text-slate-700 mb-4">Spend by Support Category</h2>
          <div className="flex-1 flex items-center justify-center">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie data={MARKET_SEGMENTS} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                  {MARKET_SEGMENTS.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v}%`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 mt-2">
            {MARKET_SEGMENTS.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />
                  <span className="text-slate-600">{s.name}</span>
                </div>
                <div className="text-right">
                  <span className="font-bold text-slate-700">{s.value}%</span>
                  <span className="text-slate-400 ml-1">{s.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* State Distribution */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-700 mb-4">Provider Distribution by State</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
          {STATE_DISTRIBUTION.map((s, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
              <div className="w-3 h-3 rounded-full" style={{ background: s.color }} />
              <div>
                <p className="text-xs text-slate-500">{s.state}</p>
                <p className="text-sm font-bold text-slate-700">{s.providers.toLocaleString()} providers</p>
                <p className="text-xs text-slate-400">{s.share}% market</p>
              </div>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={STATE_DISTRIBUTION} layout="vertical" margin={{ top: 0, right: 10, left: 20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis type="category" dataKey="state" tick={{ fontSize: 11, fill: '#94a3b8' }} width={35} />
            <Tooltip formatter={(v) => [v.toLocaleString(), 'Providers']} />
            <Bar dataKey="providers" radius={[0, 4, 4, 0]}>
              {STATE_DISTRIBUTION.map((s, i) => <Cell key={i} fill={s.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Key Trends */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <AlertCircle size={15} className="text-amber-500" />
          Key Market Trends & Opportunities
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {KEY_TRENDS.map((t, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-sm font-semibold text-slate-700">{t.title}</h3>
                <ImpactBadge level={t.impact} />
              </div>
              <p className="text-xs text-slate-500 mb-3 leading-relaxed">{t.desc}</p>
              <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-2.5">
                <p className="text-xs text-indigo-700 font-medium">Opportunity</p>
                <p className="text-xs text-indigo-600 mt-0.5">{t.opportunity}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Market context footer */}
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 border border-indigo-100 rounded-2xl p-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-xl font-bold text-indigo-700">{MARKET_STATS.techSpendPct}%</p>
            <p className="text-xs text-slate-500 mt-1">of NDIS spend on tech/software</p>
          </div>
          <div>
            <p className="text-xl font-bold text-emerald-700">${MARKET_STATS.avgProviderRevenue}M</p>
            <p className="text-xs text-slate-500 mt-1">avg provider annual revenue</p>
          </div>
          <div>
            <p className="text-xl font-bold text-amber-700">70k+</p>
            <p className="text-xs text-slate-500 mt-1">additional workers needed by 2025</p>
          </div>
          <div>
            <p className="text-xl font-bold text-violet-700">40%</p>
            <p className="text-xs text-slate-500 mt-1">increase in NDIS audits (2024)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
